import { cx } from '@libs/classnames';
import { FancyBadge } from '@ui/components/FancyBadge';
import { DesktopIcon } from '@ui/icons/DesktopIcon';
import { PhoneIcon } from '@ui/icons/PhoneIcon';

import { RoomStatus } from '@features/video/types';
import { StreamControls } from '@features/video/ui/StreamControls';
import { StreamLinkShare } from '@features/video/ui/StreamLinkShare';
import { StreamSetup, StreamSetupProps } from '@features/video/ui/StreamSetup';
import { StreamStatus } from '@features/video/ui/StreamStatus';

import styles from './StreamCamera.module.css';

export type StreamCameraMode = 'off' | 'setup' | 'device' | 'other';

export type Props = {
  className?: string;
  // заголовок блока, когда камер несколько
  title?: string;
  // требование к камере в строке заголовка
  hint?: string;
  mode: StreamCameraMode;
  // трансляция с этого устройства
  status?: RoomStatus;
  micOn?: boolean;
  camOn?: boolean;
  onToggleMic?: () => void;
  onToggleCam?: () => void;
  onHangUp?: () => void;
  // устройства: до подключения выбор и вход, в эфире те же настройки и управление
  setup?: Omit<StreamSetupProps, 'className'>;
  // другое устройство: состояние его трансляции нам неизвестно
  link?: string;
  // выбор способа
  onUseDevice?: () => void;
  onUseOther?: () => void;
  // назад к выбору способа из режима другого устройства
  onBack?: () => void;
};

export function StreamCamera({
  className,
  title,
  hint,
  mode,
  status = 'connecting',
  micOn = false,
  camOn = false,
  onToggleMic,
  onToggleCam,
  onHangUp,
  setup,
  link,
  onUseDevice,
  onUseOther,
  onBack,
}: Props) {
  const isBusy = status === 'connecting' || status === 'reconnecting';
  // для одной камеры (без заголовка) требование показываем, только когда устройство выбрано
  const showHint = !!hint && (!!title || mode !== 'off');

  return (
    <section className={cx(styles.camera, className)}>
      {(title || showHint) && (
        <h4 className={styles.camera__title}>
          {title ?? 'В кадр должны попасть'}
          {showHint && <span className={styles.camera__hint}>: {hint}</span>}
        </h4>
      )}

      {mode === 'off' && (
        <p className={styles.camera__text}>Выберите, с какого устройства снимать:</p>
      )}

      {mode === 'off' && (
        <div className={styles.camera__options}>
          <FancyBadge className={styles.camera__option} onClick={onUseDevice}>
            <span className={styles.camera__option_inner}>
              <DesktopIcon />
              С этого устройства
            </span>
          </FancyBadge>
          <FancyBadge className={styles.camera__option} onClick={onUseOther}>
            <span className={styles.camera__option_inner}>
              <PhoneIcon />С другого устройства
            </span>
          </FancyBadge>
        </div>
      )}

      {mode === 'other' && link && (
        <StreamLinkShare
          link={link}
          footer={onBack && <FancyBadge onClick={onBack}>Назад</FancyBadge>}
        />
      )}

      {mode === 'setup' && setup && <StreamSetup {...setup} className={styles.camera__setup} />}

      {mode === 'device' && setup && (
        <StreamSetup
          {...setup}
          className={styles.camera__setup}
          live={
            <>
              <StreamStatus status={status} />
              <StreamControls
                micOn={micOn}
                camOn={camOn}
                disabled={isBusy}
                onToggleMic={onToggleMic}
                onToggleCam={onToggleCam}
                onHangUp={onHangUp}
              />
            </>
          }
        />
      )}
    </section>
  );
}
